import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  getLocation,
  getWorkflowConfigurationForLocation,
} from 'src/services/api-client'
import Location from 'src/models/event/Location'
import Event from 'src/models/event/Event'
import WorkflowConfiguration from 'src/models/workflow/WorkflowConfiguration'
import { WorkflowName } from 'src/models/workflow/enums/WorkflowName'
import { PeriodType } from 'src/models/period/enums/PeriodType'
import DurationPeriod from 'src/models/period/DurationPeriod'
import { useInternationalizationStore } from 'stores/internationalization'
import {
  durationLabel,
  formatRange,
  DurationModel,
} from 'src/services/helper/booking-labels'
import { formatDurationHours } from 'src/services/helper/duration-format'
import { joinWithArrow } from 'src/services/helper/format'

/**
 * Holds the in-progress booking the URL currently points at: the location/event,
 * the location's available-workflows config, and — once inside a flow — the flow
 * type and the selected duration (exposed as reactive, locale-aware labels). The
 * URL owns the lifecycle: the store is (re)loaded for the active location and
 * cleared whenever the `locationId` route param disappears or changes (see the
 * router `afterEach` guard).
 *
 * The flow parents (`MlRental` / `MlReservation`) resolve the selected models
 * from the flow workflow config and push key + model here; labels are derived
 * reactively so they follow the active locale.
 */
export const useCurrentBookingStore = defineStore('currentBooking', () => {
  const internationalizationStore = useInternationalizationStore()

  const locationModel = ref<Location | null>(null)
  const currentEvent = ref<Event | null>(null)
  // The location's *available workflows* (which flows are active) — used by the
  // location page to show/hide the rental/reservation buttons. NOT the flow-specific
  // config (period configuration, locker types); each flow parent loads that itself.
  const availableWorkflows = ref<WorkflowConfiguration | null>(null)

  const flowType = ref<WorkflowName | null>(null)
  const periodType = ref<PeriodType | null>(null)

  // Duration selection. Rental uses the `durationKey` string; reservation uses
  // the `durationPeriod` (start/end). `durationModel` is set when the selection
  // carries its own translation (rental scheduled / reservation predefined).
  const durationKey = ref<string | null>(null)
  const durationPeriod = ref<DurationPeriod | null>(null)
  const durationModel = ref<DurationModel | null>(null)

  const currentLocationId = computed(() => locationModel.value?.id ?? null)

  const currentLocationName = computed(
    () =>
      locationModel.value?.translate(
        internationalizationStore.locale,
        internationalizationStore.fallbackLocale,
      )?.name ?? null,
  )

  const currentEventName = computed(
    () =>
      currentEvent.value?.translate(
        internationalizationStore.locale,
        internationalizationStore.fallbackLocale,
      )?.name ?? null,
  )

  // "Event ⟶ Location" — same render as `getOrderEventAndLocation` in
  // services/helper/order.ts, dropping whichever part is missing.
  const locationDisplay = computed(
    () =>
      joinWithArrow(currentEventName.value, currentLocationName.value) ?? null,
  )

  // The duration as one or more display lines:
  // - translatable model: the name, then its `subLabel` (rental scheduled) or, when
  //   it has none, the concrete window (reservation predefined).
  // - window, no model (reservation period/codelocks): the range, then total length.
  // - otherwise (rental period/codelocks/predefined): a single `durationLabel`.
  const durationLabels = computed<string[]>(() => {
    const model = durationModel.value
    if (model) {
      const translation = model.translate(
        internationalizationStore.locale,
        internationalizationStore.fallbackLocale,
      )
      const labels: string[] = []
      if (translation?.label) {
        labels.push(translation.label)
      }
      const subLabel =
        translation && 'subLabel' in translation ? translation.subLabel : null
      if (subLabel) {
        labels.push(subLabel)
      } else if (durationPeriod.value) {
        labels.push(formatRange(durationPeriod.value))
      }
      return labels
    }

    if (durationPeriod.value) {
      // Reservation PERIOD_SELECTOR / CODELOCKS: the window (arrow-separated by
      // default, matching the location line) plus its total length.
      const range = formatRange(durationPeriod.value)
      const length = formatDurationHours(
        durationPeriod.value.endDate.diff(
          durationPeriod.value.startDate,
          'hours',
        ).hours,
      )
      return length ? [range, length] : [range]
    }

    // Rental period/codelocks/predefined: a single derived label.
    const label = durationLabel({
      periodType: periodType.value,
      durationKey: durationKey.value,
    })
    return label ? [label] : []
  })

  let controller: AbortController | null = null

  /**
   * Claim the location/event synchronously (the location page holds the objects).
   * Set before the caller navigates so the router guard sees a matching
   * `currentLocationId` without waiting on the async workflow fetch. The event
   * falls back to the one nested in the location.
   */
  function setLocation(location: Location, event: Event | null = null) {
    const locationChanged = locationModel.value?.id !== location.id

    locationModel.value = location
    currentEvent.value = event ?? location.event

    // A new location invalidates the cached available-workflows so `loadWorkflow`
    // re-fetches; the same location keeps them (no redundant request).
    if (locationChanged) {
      availableWorkflows.value = null
    }
  }

  /**
   * Load the available-workflows config for the currently-set location. No-op when
   * it is already loaded for this location. Safe to call *after* the URL has been
   * updated — it keys off `locationModel` (set by `setLocation`), not its argument.
   */
  async function loadWorkflow() {
    const location = locationModel.value
    if (!location || availableWorkflows.value) {
      return
    }

    // Abort any in-flight request for a previous location.
    controller?.abort()
    controller = new AbortController()
    const requestController = controller

    const workflow = await getWorkflowConfigurationForLocation(
      location.id,
      requestController,
    )

    // Ignore results from a request that was superseded/aborted meanwhile.
    if (requestController.signal.aborted) {
      return
    }

    availableWorkflows.value = workflow
  }

  /**
   * Load the location/event from the id alone (refresh or deep-link into a flow
   * screen where the location page never mounted). The event derives from the
   * (1:1) `location.event`.
   */
  async function ensureLocation(locationId: number) {
    if (locationModel.value?.id === locationId) {
      return
    }

    const location = await getLocation(locationId)
    locationModel.value = location
    currentEvent.value = location.event
  }

  /**
   * Enter a flow (rental/reservation). The flow parent then pushes the current
   * URL selection via `setDuration`, so the store always reflects the URL
   * (see `MlRental` / `MlReservation`).
   */
  function setFlow(type: WorkflowName, period: PeriodType | null) {
    flowType.value = type
    periodType.value = period
  }

  function setDuration(
    selection: {
      key?: string | null
      period?: DurationPeriod | null
      model?: DurationModel | null
    } = {},
  ) {
    durationKey.value = selection.key ?? null
    durationPeriod.value = selection.period ?? null
    durationModel.value = selection.model ?? null
  }

  function resetSelection() {
    periodType.value = null
    durationKey.value = null
    durationPeriod.value = null
    durationModel.value = null
  }

  /**
   * Clear the flow selection (flow type, period type, duration) while keeping the
   * location/event — used when the URL leaves the flow but stays on the same
   * location. The locker type lives only in the URL, so nothing to reset here.
   */
  function clearFlow() {
    flowType.value = null
    resetSelection()
  }

  function clear() {
    controller?.abort()
    controller = null
    locationModel.value = null
    currentEvent.value = null
    availableWorkflows.value = null
    flowType.value = null
    resetSelection()
  }

  return {
    // location / event
    currentLocationId,
    locationDisplay,
    availableWorkflows,
    // flow
    flowType,
    periodType,
    // duration
    durationKey,
    durationPeriod,
    durationLabels,
    // actions
    setLocation,
    loadWorkflow,
    ensureLocation,
    setFlow,
    setDuration,
    clearFlow,
    clear,
  }
})
